import { useState, useMemo, useCallback, useEffect } from 'react';
import { Box } from '@mui/material';
import { useDispatch, useSelector } from '../../../../redux/store';
import { deleteVendors } from '../../../../redux/slices/invoicing';
import { ConfirmDialog } from '../../../../reusable-components/confirm-dialog';
import { createColumns } from '../../../../reusable-components/datagrid/custom-filters';
import CustomDataGrid from '../../../../reusable-components/datagrid/CustomDataGrid';
import { INV_VENDORS } from '../../../../reusable-components/datagrid/vendorColumns';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import NewVendorModal from './NewVendorModal';
import VendorCustomToolbar from './VendorCustomToolbar';

export default function ManualVendorTable() {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const SELECTED_VENDORS_STORAGE_KEY = 'selectedInvoiceVendors';
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectedVendors, setSelectedVendors] = useState(
    () => JSON.parse(localStorage.getItem(SELECTED_VENDORS_STORAGE_KEY)) || []
  );
  const {
    data: {
      invoicingVendors: { vendors, editableVendorBookingTypes },
    },
    isLoading,
  } = useSelector((state) => state.invoicing);
  const data = vendors ?? [];
  useEffect(() => {
    const selectableRows = new Set(data.filter((row) => isRowEditable(row)).map((row) => row.id));
    const validSelections = selectedVendors.filter((id) => selectableRows.has(id));

    if (validSelections.length !== selectedVendors.length) {
      updateSelectedVendors(validSelections);
    }
  }, [data, editableVendorBookingTypes]);

  const isRowEditable = (row) => editableVendorBookingTypes?.some((f) => f.id === row.bookingTypeId);

  const updateSelectedVendors = (selection) => {
    setSelectedVendors(selection);
    if (selection.length > 0) {
      localStorage.setItem(SELECTED_VENDORS_STORAGE_KEY, JSON.stringify(selection));
    } else {
      localStorage.removeItem(SELECTED_VENDORS_STORAGE_KEY);
    }
  };
  const handleDeleteVendors = useCallback(
    async (ids) => {
      const res = await dispatch(deleteVendors(ids));
      enqueueSnackbar(res.message ?? 'Error deleting vendor(s)', { variant: res.success ? 'success' : 'error' });
      if (ids.length > 1) {
        updateSelectedVendors([]);
        setIsSelecting(false);
      }
    },
    [dispatch, enqueueSnackbar]
  );

  const customToolbar = useCallback(
    () => (
      <VendorCustomToolbar
        isSelecting={isSelecting}
        setIsSelecting={setIsSelecting}
        selectedVendors={selectedVendors}
        updateSelectedVendors={updateSelectedVendors}
        handleBulkDelete={() => handleDeleteVendors(selectedVendors)}
      />
    ),
    [isSelecting, selectedVendors.length]
  );

  const actionButtons = useMemo(
    () => ({
      field: 'actions',
      headerName: 'Actions',
      disableReorder: true,
      flex: 1,
      renderCell: (params) => (
        <>
          {editableVendorBookingTypes?.find((f) => f.id === params.row.bookingTypeId) && (
            <>
              <ConfirmDialog
                icon="delete-outline"
                title="Delete"
                color="error"
                content="Are you sure you want to delete this vendor?"
                actionButton="Delete"
                action={() => handleDeleteVendors([params.row.id])}
              />
              <Box>
                <NewVendorModal row={params.row} edit />
              </Box>
            </>
          )}
        </>
      ),
      id: 'actions',
    }),
    [editableVendorBookingTypes, handleDeleteVendors]
  );

  const columns = useMemo(
    () => createColumns([...INV_VENDORS, actionButtons]),
    [actionButtons, editableVendorBookingTypes]
  );

  return (
    <CustomDataGrid
      gridId="invoicing-vendors-invoicing-vendors"
      boxSX={{ height: 'calc(100vh - 280px)' }}
      data={data}
      sort={[{ field: 'invoiceVendorId', sort: 'asc' }]}
      gridColumns={[...columns]}
      CustomLeftToolbar={customToolbar}
      checkboxSelection={isSelecting}
      isRowSelectable={(params) => isRowEditable(params.row)}
      rowSelectionModel={selectedVendors}
      onRowSelectionModelChange={(newSelection) => {
        updateSelectedVendors(newSelection || []);
      }}
      onCellClick={(row, event, api) => {
        const selection = window.getSelection().toString();
        const apiObj = api.api;

        if (selection.length > 0 || !isSelecting) return;
        if (!isRowEditable(row.row)) return;

        const isSelected = apiObj.isRowSelected(row.id);
        apiObj.selectRow(row.id, !isSelected);
      }}
      isLoading={isLoading}
    />
  );
}
